import { BaseColours } from './baseColours';
import { IEoraThemeColours } from './eoraColourUtils';
import { ExtraBrandColour, MainBrandColour } from './variants';

export const BASE_STATE_KEYS = {
  HOVER: 'hover',
  ACTIVE: 'active'
} as const;
export type BaseStateKey = (typeof BASE_STATE_KEYS)[keyof typeof BASE_STATE_KEYS];

const EXTENDED_BASE_STATE_KEYS = {
  disabled: 'disabled',
  selected: 'selected',
  focus: 'focus'
} as const;
type ExtendedStateKey = (typeof EXTENDED_BASE_STATE_KEYS)[keyof typeof EXTENDED_BASE_STATE_KEYS];

const STATE_COLOUR_KEYS = {
  colour: 'colour',
  text: 'text' // use this as text colour when `colour` is used as bg
} as const;
type StateColourKey = (typeof STATE_COLOUR_KEYS)[keyof typeof STATE_COLOUR_KEYS];

export type StateColourType = { [K in StateColourKey | BaseStateKey]: string };
export type StateColour = StateColourType & {
  /**
   * @deprecated EORA V2 no longer has light colours
   */
  light?: StateColourType;
  link?: string;
};

export const THEME_COMPONENT_COLOUR_KEYS = {
  text: 'text',
  background: 'background',
  border: 'border',
  boxShadow: 'boxShadow',
  placeholder: 'placeholder',
  caret: 'caret',
  outline: 'outline'
} as const;
type ThemeComponentColourKey = (typeof THEME_COMPONENT_COLOUR_KEYS)[keyof typeof THEME_COMPONENT_COLOUR_KEYS];

export const THEME_MISC_KEYS = {
  OPACITY: 'opacity',
  TEXT_DECORATION: 'textDecoration'
} as const;

export type ColourAssignment = { [K in ThemeComponentColourKey]?: string } & {
  [THEME_MISC_KEYS.OPACITY]?: number;
  [THEME_MISC_KEYS.TEXT_DECORATION]?: string;
};

export type ColourState = { [K in BaseStateKey | ExtendedStateKey]?: ColourAssignment };

export type MapColourStates = ColourAssignment & ColourState;

// Note: used in testing theme
export const VALID_THEME_KEY_SELECTORS = {
  ...THEME_COMPONENT_COLOUR_KEYS,
  ...STATE_COLOUR_KEYS,
  ...BASE_STATE_KEYS,
  ...THEME_MISC_KEYS
};

export interface BrandColours extends Record<ExtraBrandColour, StateColour> {}
export interface MainBrandColours extends Record<MainBrandColour | ExtraBrandColour, StateColour> {}

export interface IThemeColours {
  brandColours: BrandColours | MainBrandColours;
  baseColours: BaseColours;
  eoraColours: IEoraThemeColours;
}

export type IColoursCollection = BaseColours & BrandColours & IEoraThemeColours;

export type LinkStates = Record<BaseStateKey, string> & {
  [STATE_COLOUR_KEYS.colour]: string;
  visited: string;
};

export type PaletteKey =
  | 'info25'
  | 'info50'
  | 'info100'
  | 'info200'
  | 'info300'
  | 'info400'
  | 'info' // Default colour = 500
  | 'info600'
  | 'info700'
  | 'info800'
  | 'info900'
  | 'success25'
  | 'success50'
  | 'success100'
  | 'success200'
  | 'success300'
  | 'success400'
  | 'success' // Default colour = 500
  | 'success600'
  | 'success700'
  | 'success800'
  | 'success900'
  | 'warning25'
  | 'warning50'
  | 'warning100'
  | 'warning200'
  | 'warning300'
  | 'warning400'
  | 'warning' // Default colour = 500
  | 'warning600'
  | 'warning700'
  | 'warning800'
  | 'warning900'
  | 'error25'
  | 'error50'
  | 'error100'
  | 'error200'
  | 'error300'
  | 'error400'
  | 'error' // Default colour = 500
  | 'error600'
  | 'error700'
  | 'error800'
  | 'error900'
  | 'neutral5'
  | 'neutral15'
  | 'neutral25'
  | 'neutral50'
  | 'neutral100'
  | 'neutral200'
  | 'neutral300'
  | 'neutral400'
  | 'neutral' // Default colour = 500
  | 'neutral600'
  | 'neutral700'
  | 'neutral800'
  | 'neutral900'
  | 'mint25'
  | 'mint50'
  | 'mint100'
  | 'mint200'
  | 'mint300'
  | 'mint400'
  | 'mint' // Default colour = 500
  | 'mint600'
  | 'mint700'
  | 'mint800'
  | 'mint900'
  | 'rose25'
  | 'rose50'
  | 'rose100'
  | 'rose200'
  | 'rose300'
  | 'rose400'
  | 'rose' // Default colour = 500
  | 'rose600'
  | 'rose700'
  | 'rose800'
  | 'rose900'
  | 'sky25'
  | 'sky50'
  | 'sky100'
  | 'sky200'
  | 'sky300'
  | 'sky400'
  | 'sky' // Default colour = 500
  | 'sky600'
  | 'sky700'
  | 'sky800'
  | 'sky900';
